import isEmpty from 'lodash/isEmpty'
import type { UIMessage, UIMessagePart } from 'ai'
import type { ReactNode } from 'react'
import { Stack } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { HorizontalStack, HtmlMarkdownRenderer, Icon } from '@ztechapps/webui.util'
import { useUserTimeZone } from '@ztechapps/common.hook'
import { formatDate } from '@ztechapps/common.utils'
import { ToolCallDisplay, type ToolPart } from './ToolCallDisplay'
import { coalesceCreateProductToolParts } from './coalesceCreateProductToolParts'
import { DownloadBlock } from './DownloadBlock'
import { toInAppPath } from './toInAppPath'
import { useAiChatContext } from './Context'
import styles from './styles.module.css'

type MessageBubbleProps = {
  /** The message to display */
  message: UIMessage
}

/**
 * MessageBubble component displays a single message in the chat.
 * User messages are displayed on the right, assistant messages on the left.
 * Supports markdown rendering and tool call display.
 */
export const MessageBubble = ({ message }: MessageBubbleProps) => {
  const { assistantName } = useAiChatContext()
  const timeZone = useUserTimeZone()
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'

  // Don't display system messages
  if (message.role === 'system') {
    return null
  }

  // Extract text content from parts
  const textParts =
    message.parts?.filter((part): part is Extract<UIMessagePart<any, any>, { type: 'text' }> => part.type === 'text') ??
    []
  const textContent = textParts.map((part) => part.text).join('\n')

  // Extract tool parts (all parts that start with 'tool-' or are dynamic-tool).
  // Coalesce multiple sibling `createProduct` parts into one aggregated render
  // so an LLM that fires N separate createProduct calls in a single turn still
  // surfaces a single proposal grid instead of N stacked cards.
  const rawToolParts = (message.parts?.filter(
    (part) => part.type.startsWith('tool-') || part.type === 'dynamic-tool'
  ) ?? []) as ToolPart[]
  const toolParts = coalesceCreateProductToolParts(rawToolParts)

  // Extract file parts
  const fileParts =
    message.parts?.filter((part): part is Extract<UIMessagePart<any, any>, { type: 'file' }> => part.type === 'file') ??
    []

  return (
    <div className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'} mb-2`}>
      <div
        className={`
          p-3 rounded-3 ${styles.messageBubble}
          ${isUser ? 'bg-primary-subtle' : 'bg-secondary-subtle text-body'}
        `}
      >
        {/* Message header with role indicator */}
        <HorizontalStack gap={2} className="mb-1 small opacity-75">
          <Icon name={isUser ? 'user' : 'robot'} />
          <span>{isUser ? 'You' : assistantName}</span>
          <span className="ms-auto">{formatDate(new Date(), 'HH:mm', timeZone)}</span>
        </HorizontalStack>

        {/* Message content */}
        {textContent && <MessageContent content={textContent} isUser={isUser} />}

        {/* File attachments */}
        {!isEmpty(fileParts) && (
          <Stack gap={1} className="mt-2">
            {fileParts.map((part, index) => (
              <AttachmentPreview key={index} attachment={{ url: part.url, contentType: part.mediaType }} />
            ))}
          </Stack>
        )}

        {/* Tool invocations (when AI is calling tools) */}
        {isAssistant && !isEmpty(toolParts) && (
          <Stack gap={2} className="mt-2">
            {toolParts.map((toolPart) => (
              <ToolCallDisplay key={toolPart.toolCallId} toolInvocation={toolPart} />
            ))}
          </Stack>
        )}
      </div>
    </div>
  )
}

/**
 * Renders message content - handles string content
 */
const MessageContent = ({ content, isUser }: { content: string; isUser: boolean }) => {
  if (!content) {
    return null
  }

  if (isUser) {
    // Plain text for user messages
    return <div className={styles.userMessageContent}>{content}</div>
  }

  // Markdown rendering for assistant messages with custom components
  return (
    <div className={styles.markdownContent}>
      <HtmlMarkdownRenderer
        components={{
          a: ({ href, children, node: _node, ...rest }) => {
            const inAppPath = toInAppPath(href)
            return inAppPath ? (
              <Link to={inAppPath} {...rest}>
                {children}
              </Link>
            ) : (
              <a href={href} {...rest}>
                {children}
              </a>
            )
          },
          code: (props: { className?: string; children?: ReactNode; node?: any }) => {
            const { className, children, node } = props

            // Check if this is inline code by looking at the node structure
            const isInline =
              node?.position?.start?.line === node?.position?.end?.line &&
              !node?.properties?.className?.includes('language-')

            const match = /language-(\w+)/.exec(className || '')
            const language = match?.[1]

            // Handle download blocks - special code blocks for file downloads
            if (language === 'download' && !isInline) {
              return <DownloadBlock>{String(children)}</DownloadBlock>
            }

            // Handle regular code blocks
            if (!isInline && language) {
              return (
                <pre className={className}>
                  <code>{children}</code>
                </pre>
              )
            }

            // Handle inline code
            return <code className={className}>{children}</code>
          },
        }}
      >
        {content}
      </HtmlMarkdownRenderer>
    </div>
  )
}

/**
 * Attachment from the Message type
 */
type MessageAttachment = {
  name?: string
  contentType?: string
  url: string
}

/**
 * Attachment preview component
 */
const AttachmentPreview = ({ attachment }: { attachment: MessageAttachment }) => {
  const isPdf = attachment.contentType?.includes('pdf') || attachment.name?.endsWith('.pdf')
  const isImage = attachment.contentType?.startsWith('image/')

  if (isPdf) {
    return (
      <HorizontalStack gap={2} className="p-2 bg-body-secondary rounded">
        <Icon name="file-pdf" className="text-danger" />
        <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="text-body text-decoration-none">
          {attachment.name ?? 'View PDF'}
        </a>
      </HorizontalStack>
    )
  }

  if (isImage) {
    return (
      <a href={attachment.url} target="_blank" rel="noopener noreferrer">
        <img
          src={attachment.url}
          alt={attachment.name ?? 'Attachment'}
          className={`rounded ${styles.attachmentImage}`}
        />
      </a>
    )
  }

  // Generic file attachment
  return (
    <HorizontalStack gap={2} className="p-2 bg-body-secondary rounded">
      <Icon name="file" className="text-secondary" />
      <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="text-body text-decoration-none">
        {attachment.name ?? 'View file'}
      </a>
    </HorizontalStack>
  )
}
